import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import UploadImageIcon from 'apps/web_forms/assets/photogallery.svg';
import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import ThumbnailImage from 'apps/web_forms/components/commons/ThumbnailImage';
import useLocalImage from 'apps/web_forms/hooks/useLocalImage';

import { removeImageDraftValue } from '../../slices/multimediaSlice';
import { removeDispatchAnswer } from '../../slices/formSlice';
import { COLORS, DEVICES_SIZES, STYLES } from 'apps/web_forms/utils/constants';

import { CameraButton } from 'apps/web_forms/components/commons/CameraButton';
import { FileNameInput } from 'apps/web_forms/components/inputs/FileNameInput';
import { FileUploadLabel } from 'apps/web_forms/components/Uploads/FileUploadLabel';
import { useDragAndDrop}  from 'apps/web_forms/hooks/useDragAndDrop';
import { useImagePreview } from 'apps/web_forms/hooks/useImagePreview';
import { useImageValidation } from 'apps/web_forms/hooks/useImageValidation';

const StyledButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: no-wrap;
  width: 100%;
  padding: 0.9em 0;
  &.readOnly {
    background-color: ${COLORS.readOnlyInputBackgroundColor};
    opacity: 0.5;
    padding-left: 0.9em;
    #upload-file-label,
    .camera-button {
      cursor: default;
    }
  }
  p {
    margin: 0 0.5em;
    font-size: inherit;
  }
  #file-name-field {
    width: 40%;
    cursor: default;
    transition: 200ms;
  }
  #file-name-field.drag-enter {
    transform: scale(1.05);
    border: 1px solid ${COLORS.green};
  }
  #upload-file-label {
    cursor: pointer;
    margin-bottom: 0;
    margin-left: 0.5em;
    display: flex;
    align-items: center;
    svg {
      border: 1px solid transparent;
    }
    p {
      font-weight: normal;
    }
    &.required,
    &.invalid {
      svg {
        border: ${STYLES.ErrorHighlight};
        border-radius: 0.45em;
      }
    }
    &.updateHighlight {
      svg {
        border: ${STYLES.UpdateHighlight};
        border-radius: 0.45em;
      }
    }
  }
  input[type='file'] {
    display: none;
  }
  .camera-button {
    background-color: transparent;
    border: none;
    display: flex;
    align-items: center;
    font-size: 0.95em;
    svg {
      border: 1px solid transparent;
    }
    &.required,
    &.invalid {
      svg {
        border: ${STYLES.ErrorHighlight};
        border-radius: 0.55em;
      }
    }
    &.updateHighlight {
      svg {
        border: ${STYLES.UpdateHighlight};
        border-radius: 0.55em;
      }
    }
  }
  @media (min-width: ${DEVICES_SIZES.tablet}) {
    #upload-file-label {
      width: auto;
    }
    .camera-button {
      width: auto;
    }
  }
`;

const ImageUploadElement = React.forwardRef(
  (
    {
      id,
      hint,
      cameraButtonIcon: CameraButtonIcon,
      cameraOnly,
      thumbnailImage,
      textValue,
      allowTextOverride,
      onChangeText,
      isRequired,
      isInvalid,
      hasResourceUpdateHighlight,
      removeResourceUpdateHighlight,
      isReadOnly,
      imageFromCamera,
      onCameraButtonClick,
      onNewImage,
      removeImageToSubmit,
      changeValue,
      noThumbnail,
      compressImage,
      initialAnswer,
      hasValue,
      imageDraftValues,
      imagesToSubmit,
      formContext,
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const { formState } = formContext;
    const dispatchAnswer = useSelector((state) => state.form.dispatchAnswers[id]);

    // useLocalImage handles file selection, file name, errors, and cleaning data.
    const {
      handleFiles,
      currentFile,
      uploadErrorMessage,
      currentFileName,
      cleanImageData,
    } = useLocalImage({ onReupload: () => onNewImage(imageState.image) });

    // Image validation hook
    const { validateImage, error: validationError } = useImageValidation();

    // Image state
    const [imageState, setImageState] = useState({
      image: 'initial',
      isDirty: false,
    });

    // Set image from initialAnswer if available.
    useEffect(() => {
      if (initialAnswer && !imagesToSubmit[id] && !formState.dirtyFields?.[id]) {
        setImageState({
          image: `data:image/jpeg;base64,${initialAnswer}`,
          isDirty: false,
        });
      }
    }, [initialAnswer, formState.dirtyFields, id, imagesToSubmit]);

    // Update image state if a draft value exists.
    useEffect(() => {
      if (
        imageDraftValues &&
        imageDraftValues[id] &&
        imageDraftValues[id].imageUrl !== undefined
      ) {
        setImageState({
          image: imageDraftValues[id].imageUrl,
          isDirty: false,
        });
        dispatch(removeImageDraftValue(id));
        if (dispatchAnswer) {
          dispatch(removeDispatchAnswer(id));
        }
      }
    }, [imageDraftValues, id, dispatch, dispatchAnswer]);

    // Update state when a new file is selected.
    useEffect(() => {
      if (currentFile) {
        setImageState({ image: currentFile, isDirty: true });
      }
    }, [currentFile]);

    // Update state when an image is captured from the camera.
    useEffect(() => {
      if (imageFromCamera) {
        cleanImageData();
        setImageState({ image: imageFromCamera, isDirty: true });
      }
    }, [imageFromCamera]);

    const { dropRef, dragEnter } = useDragAndDrop((e) => {
      const { files } = e.dataTransfer;
      cleanImageData();
      const file = files[0];
      if (!validateImage(file)) {
        return;
      }
      handleFiles(files, compressImage);
    });

    // Custom image preview hook.
    const { thumbnailUrl, createImageUrl } = useImagePreview();

    // When imageState changes, update parent value, preview, and notify of a new image.
    useEffect(() => {
      if (imageState.image !== 'initial') {
        if (imageState.image) {
          changeValue(imageState.image.slice(0, 20), imageState.isDirty);
          createImageUrl(imageState.image);
          onNewImage(imageState.image);
        } else {
          cleanImageData();
          changeValue('', true);
          removeImageToSubmit();
        }
      }
    }, [imageState]);

    const handleCloseButtonClick = useCallback(() => {
      if (!isReadOnly) {
        setImageState({ image: undefined, isDirty: true });
        if (imageDraftValues?.[id]) {
          dispatch(removeImageDraftValue(id));
        }
        if (dispatchAnswer) {
          dispatch(removeDispatchAnswer(id));
        }
      }
    }, [isReadOnly, id, imageDraftValues, dispatch, dispatchAnswer]);

    return (
      <>
        <StyledButtonsContainer className={isReadOnly ? 'readOnly' : ''}>
          <CameraButton
            onClick={onCameraButtonClick}
            onFocus={removeResourceUpdateHighlight}
            isRequired={isRequired && !hasValue}
            isInvalid={isInvalid && !hasValue}
            isReadOnly={isReadOnly}
            hasResourceUpdateHighlight={hasResourceUpdateHighlight}
          >
            <CameraButtonIcon />
          </CameraButton>

          {!cameraOnly && (
            <>
              <FileNameInput
                ref={dropRef}
                value={textValue ?? currentFileName}
                onChange={onChangeText}
                dragEnter={dragEnter}
                hasResourceUpdateHighlight={hasResourceUpdateHighlight}
                placeholder={
                  dragEnter ? 'Drop image to upload' : 'Drag or upload an image'
                }
                readOnly={!allowTextOverride}
                onFocus={removeResourceUpdateHighlight}
              />
              <FileUploadLabel
                id={id}
                fileInputRef={useRef(null)}
                isRequired={isRequired && !hasValue}
                isInvalid={isInvalid && !hasValue}
                hasResourceUpdateHighlight={hasResourceUpdateHighlight}
                removeResourceUpdateHighlight={removeResourceUpdateHighlight}
                handleFiles={(files) => {
                  const file = files[0];
                  if (!validateImage(file)) {
                    return;
                  }
                  handleFiles(files, compressImage);
                }}
                UploadIcon={UploadImageIcon}
                disabled={isReadOnly}
              />
            </>
          )}
          <input name={id} ref={ref} type="text" hidden />
        </StyledButtonsContainer>
        {thumbnailImage && !noThumbnail && (
          <ThumbnailImage
            identifier={id}
            srcValue={thumbnailImage}
            thumbnailUrl={thumbnailUrl}
            onClose={handleCloseButtonClick}
          />
        )}
        {hint && <p className="element-hint">{hint}</p>}
        {uploadErrorMessage !== undefined && (
          <ErrorMessage id={`${id}_upload_errmsg`}>
            {uploadErrorMessage}
          </ErrorMessage>
        )}
        {/* Optionally render validation error */}
        {validationError && <p className="error">{validationError}</p>}
      </>
    );
  }
);

ImageUploadElement.propTypes = {
  id: PropTypes.string.isRequired,
  hint: PropTypes.string,
  cameraButtonIcon: PropTypes.elementType.isRequired,
  cameraOnly: PropTypes.bool,
  thumbnailImage: PropTypes.string,
  textValue: PropTypes.string,
  allowTextOverride: PropTypes.bool,
  onChangeText: PropTypes.func,
  isRequired: PropTypes.bool,
  isInvalid: PropTypes.bool,
  hasResourceUpdateHighlight: PropTypes.bool,
  removeResourceUpdateHighlight: PropTypes.func,
  isReadOnly: PropTypes.bool,
  imageFromCamera: PropTypes.string,
  onCameraButtonClick: PropTypes.func,
  onNewImage: PropTypes.func,
  removeImageToSubmit: PropTypes.func,
  changeValue: PropTypes.func,
  noThumbnail: PropTypes.bool,
  compressImage: PropTypes.bool,
  initialAnswer: PropTypes.string,
  hasValue: PropTypes.bool,
  imageDraftValues: PropTypes.object,
  imagesToSubmit: PropTypes.object,
  formContext: PropTypes.object.isRequired,
};

ImageUploadElement.defaultProps = {
  hint: '',
  cameraOnly: false,
  thumbnailImage: undefined,
  textValue: undefined,
  allowTextOverride: false,
  isRequired: false,
  isInvalid: false,
  hasResourceUpdateHighlight: false,
  removeResourceUpdateHighlight: () => {},
  isReadOnly: false,
  imageFromCamera: undefined,
  noThumbnail: false,
  compressImage: false,
  onCameraButtonClick: () => {},
  onNewImage: () => {},
  onChangeText: () => {},
  changeValue: () => {},
  removeImageToSubmit: () => {},
  initialAnswer: '',
  hasValue: false,
  imageDraftValues: {},
  imagesToSubmit: {},
};

export default ImageUploadElement;
